// @spec AST-STR-028
'use client';

import { createFileRoute, useNavigate, useSearch } from '@tanstack/react-router';
import * as React from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { GapDashboard, useGapDashboard } from '@/features/gaps';
import type { GapDashboardFilters } from '@/features/gaps/types';
import { useOrganizations } from '@/hooks/use-organizations';
import i18n from '@/lib/i18n';
import { useTranslation } from 'react-i18next';

// URL search params schema for validation
const gapDashboardSearchSchema = z.object({
  severity: z.enum(['critical', 'high', 'medium', 'low']).optional(),
  assetCategory: z
    .enum(['it_systems', 'physical_assets', 'information_assets', 'people_vendors'])
    .optional(),
  ruleId: z.string().uuid().optional(),
  dateFrom: z.string().optional(),
  dateTo: z.string().optional(),
});

export const Route = createFileRoute('/_authenticated/gaps/')({
  validateSearch: gapDashboardSearchSchema,
  beforeLoad: () => ({
    getTitle: () => i18n.t('routeTitles.gapAnalysisDashboard'),
  }),
  component: GapDashboardPage,
});

/**
 * Gap dashboard page - main entry point for control gap analysis.
 */
function GapDashboardPage() {
  const { t } = useTranslation('common');
  const navigate = useNavigate();
  const search = useSearch({ from: '/_authenticated/gaps/' });
  const { organizations, isLoading: orgsLoading } = useOrganizations();

  // Get active organization
  const activeOrg = organizations.find((org) => org.isActive);

  // Extract filter state from URL search params
  const filters: GapDashboardFilters = React.useMemo(
    () => ({
      severity: search.severity ? [search.severity] : null,
      assetCategory: search.assetCategory ?? null,
      ruleId: search.ruleId ?? null,
      dateFrom: search.dateFrom ?? null,
      dateTo: search.dateTo ?? null,
    }),
    [search]
  );

  // Fetch dashboard data with current filters
  const {
    data: dashboardData,
    isLoading: dashboardLoading,
    error: dashboardError,
    refetch,
  } = useGapDashboard({
    orgId: activeOrg?.id ?? '',
    severity: filters.severity,
    assetCategory: filters.assetCategory,
    ruleId: filters.ruleId,
    dateFrom: filters.dateFrom,
    dateTo: filters.dateTo,
    enabled: Boolean(activeOrg),
  });

  // Update filters in URL
  const handleFiltersChange = React.useCallback(
    (newFilters: GapDashboardFilters) => {
      void navigate({
        search: {
          severity: newFilters.severity?.[0] ?? undefined,
          assetCategory: newFilters.assetCategory ?? undefined,
          ruleId: newFilters.ruleId ?? undefined,
          dateFrom: newFilters.dateFrom ?? undefined,
          dateTo: newFilters.dateTo ?? undefined,
        } as never,
        replace: true,
      });
    },
    [navigate]
  );

  // Handle severity filter click
  const handleSeverityClick = React.useCallback(
    (severity: string) => {
      handleFiltersChange({
        ...filters,
        severity: [severity as 'critical' | 'high' | 'medium' | 'low'],
      });
    },
    [filters, handleFiltersChange]
  );

  // Handle category filter click
  const handleCategoryClick = React.useCallback(
    (category: string) => {
      handleFiltersChange({
        ...filters,
        assetCategory: category as
          | 'it_systems'
          | 'physical_assets'
          | 'information_assets'
          | 'people_vendors',
      });
    },
    [filters, handleFiltersChange]
  );

  // Handle asset click - navigate to asset detail
  const handleAssetClick = React.useCallback(
    (assetId: string) => {
      void navigate({
        to: '/assets/$assetId',
        params: { assetId },
      });
    },
    [navigate]
  );

  // Show loading while checking organizations
  if (orgsLoading) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="animate-pulse space-y-4">
            <div className="h-8 w-48 rounded bg-muted" />
            <div className="h-64 rounded bg-muted" />
          </div>
        </div>
      </div>
    );
  }

  // No active organization selected
  if (!activeOrg) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-7xl">
          <div className="rounded-lg border border-dashed p-12 text-center">
            <h2 className="text-lg font-semibold text-foreground">{t('common:routeErrors.noOrgSelected')}</h2>
            <p className="mt-2 text-muted-foreground">
              {t('common:routeErrors.noOrgSelectedDesc')}
            </p>
            <Button
              variant="outline"
              onClick={() => void navigate({ to: '/settings/organizations' })}
              className="mt-4"
            >
              {t('common:routeErrors.goToOrganizations')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background p-8">
      <div className="mx-auto max-w-7xl space-y-6">
        {/* Header */}
        <header className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-semibold text-foreground">Gap Analysis Dashboard</h1>
            <p className="mt-1 text-muted-foreground">Control gaps for {activeOrg.name}</p>
          </div>
        </header>

        {/* Dashboard */}
        <GapDashboard
          data={dashboardData}
          isLoading={dashboardLoading}
          error={dashboardError}
          filters={filters}
          onFiltersChange={handleFiltersChange}
          onAssetClick={handleAssetClick}
          onSeverityClick={handleSeverityClick}
          onCategoryClick={handleCategoryClick}
          onRefresh={() => void refetch()}
        />
      </div>
    </div>
  );
}
